import { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { spacing, fontStyles, colors } from '../../styles';
import * as storage from '../../storage';
import { getBasicInvestments } from '../../apis/backend';
import { investmentIndex } from '../../apis/search';
import Page from '../Page';
import Header from '../Header';
import NavigationBar from '../NavigationBar';
import Button from '../Button';
import deleteIcon from './icons/delete.svg';
import { PORTFOLIO_STORAGE_KEY } from './portfolioSelection';

interface RouteParams {
  id: string;
}

const EditPortfolio: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<RouteParams>();
  const history = useHistory();

  const [portfolio, setPortfolio] = useState<storage.Portfolio | null>(null);
  const [portfolioTitle, setPortfolioTitle] = useState<string>('');
  const [localAllocations, setLocalAllocations] = useState<storage.Allocation[]>([]);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [validationStatus, setValidationStatus] = useState<Record<number, { isValid: boolean; isValidating: boolean }>>(
    {},
  );
  const [allocationStatus, setAllocationStatus] = useState<Record<number, { isEmpty: boolean; isValid: boolean }>>({});

  // Track original data for change detection
  const [originalData, setOriginalData] = useState<{ title: string; allocations: storage.Allocation[] } | null>(null);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!originalData) return false;

    // Check title change
    if (portfolioTitle !== originalData.title) return true;

    // Check allocations changes
    if (localAllocations.length !== originalData.allocations.length) return true;

    for (let i = 0; i < localAllocations.length; i++) {
      const current = localAllocations[i];
      const original = originalData.allocations[i];

      if (!original) return true; // New allocation
      if (current.symbol !== original.symbol) return true;
      if (current.allocation_percentage !== original.allocation_percentage) return true;
    }

    return false;
  };

  // Handle back button with unsaved changes warning
  const handleBack = () => {
    if (hasUnsavedChanges()) {
      if (window.confirm(t('portfolios.edit.unsavedChanges'))) {
        history.goBack();
      }
    } else {
      history.goBack();
    }
  };

  // Load portfolio data
  useEffect(() => {
    const loadPortfolio = async () => {
      setIsLoading(true);
      try {
        // Check if this is a new portfolio creation
        if (id === 'new') {
          // Create new empty portfolio
          const newPortfolio: storage.Portfolio = {
            id: 'temp-new-id', // This will be replaced when saved
            title: 'New Portfolio',
            strategy: '',
            symbols: [],
            allocations: [],
          };
          setPortfolio(newPortfolio);
          setPortfolioTitle('New Portfolio');
          setLocalAllocations([
            {
              symbol: '',
              name: '',
              allocation_percentage: undefined,
              rationale: '',
            },
          ]);
          // Set original data for new portfolio
          setOriginalData({ title: 'New Portfolio', allocations: [] });
          setIsLoading(false);
          return;
        }

        // Load existing portfolio
        const portfolios = await storage.getPortfolios();
        const foundPortfolio = portfolios.find((p) => p.id === id);
        if (foundPortfolio) {
          setPortfolio(foundPortfolio);
          setPortfolioTitle(foundPortfolio.title);

          // Check if portfolio has symbols but empty allocations (watchlist case)
          if (
            foundPortfolio.symbols?.length > 0 &&
            (!foundPortfolio.allocations || foundPortfolio.allocations.length === 0)
          ) {
            // Convert symbols array to allocations format
            const allocationsFromSymbols = foundPortfolio.symbols.map((symbol) => ({
              symbol,
              name: '',
              allocation_percentage: undefined,
              rationale: '',
            }));
            setLocalAllocations(allocationsFromSymbols);
            // Set original data
            setOriginalData({ title: foundPortfolio.title, allocations: allocationsFromSymbols });
          } else {
            // Normal case: use existing allocations
            const allocations = foundPortfolio.allocations || [];
            setLocalAllocations(allocations);
            // Set original data
            setOriginalData({ title: foundPortfolio.title, allocations });
          }
        }
      } catch (error) {
        console.error('Error loading portfolio:', error);
        setError(t('portfolios.edit.failedLoad'));
      } finally {
        setIsLoading(false);
      }
    };

    loadPortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // No longer needed as AutoGrowTextarea handles height adjustment internally

  const handleSymbolChange = (index: number, newSymbol: string): void => {
    const updated = [...localAllocations];
    updated[index] = {
      ...updated[index],
      symbol: newSymbol.toUpperCase(),
    };
    setLocalAllocations(updated);

    // Only validate if symbol has at least 1 character
    if (newSymbol.trim().length > 0) {
      // Set validating state
      setValidationStatus((prev) => ({
        ...prev,
        [index]: { isValid: false, isValidating: true },
      }));

      // Debounce validation to avoid too many API calls
      const symbolToValidate = newSymbol.toUpperCase();
      setTimeout(async () => {
        try {
          const isValid = await validateTicker(symbolToValidate);
          // Only update if this is still the current symbol
          if (symbolToValidate === updated[index].symbol) {
            setValidationStatus((prev) => ({
              ...prev,
              [index]: { isValid, isValidating: false },
            }));
          }
        } catch (error) {
          console.error('Error during validation:', error);
          setValidationStatus((prev) => ({
            ...prev,
            [index]: { isValid: false, isValidating: false },
          }));
        }
      }, 600); // Delay validation by 600ms
    } else {
      // Clear validation for empty symbols
      setValidationStatus((prev) => {
        const newStatus = { ...prev };
        delete newStatus[index];
        return newStatus;
      });
    }
  };

  const handleAllocationChange = (index: number, newAllocation: string) => {
    // Allow empty string
    if (newAllocation === '') {
      const updated = [...localAllocations];
      updated[index] = {
        ...updated[index],
        allocation_percentage: undefined,
      };
      setLocalAllocations(updated);

      // For watchlists, undefined allocation is valid
      setAllocationStatus((prev) => ({
        ...prev,
        [index]: { isEmpty: true, isValid: true },
      }));
      return;
    }

    const value = parseFloat(newAllocation);
    if (isNaN(value)) return;

    // Validate the range but still allow any input
    const isValid = value >= 0 && value <= 100;

    const updated = [...localAllocations];
    updated[index] = {
      ...updated[index],
      allocation_percentage: value,
    };
    setLocalAllocations(updated);

    // Update validation status
    setAllocationStatus((prev) => ({
      ...prev,
      [index]: { isEmpty: false, isValid },
    }));
  };

  const handleRemove = (index: number) => {
    const updated = localAllocations.filter((_, i) => i !== index);
    setLocalAllocations(updated);
  };

  const handleAddTicker = () => {
    setLocalAllocations([
      ...localAllocations,
      {
        symbol: '',
        name: '',
        allocation_percentage: undefined,
        rationale: '',
      },
    ]);
  };

  // Validate a single ticker using the search API
  const validateTicker = async (ticker: string): Promise<boolean> => {
    if (!ticker.trim()) return false;

    try {
      // For hyphenated tickers like BRK-B, Algolia tokenizes on the hyphen so
      // searching "BRK-B" may not return it in the top results. Search the base
      // symbol instead (e.g. "BRK") to get a broader result set, then exact-match.
      const searchQuery = ticker.includes('-') ? ticker.split('-')[0] : ticker;
      const searchResults = await investmentIndex.search(searchQuery, {
        hitsPerPage: 50,
      });

      // Check if we have an exact match for the symbol
      return searchResults.hits.some((hit: any) => hit.symbol.toUpperCase() === ticker.toUpperCase());
    } catch (error) {
      console.error('Error validating ticker:', error);
      return false;
    }
  };

  const handleSave = async () => {
    if (!portfolio) return;

    setError('');
    setIsSaving(true);

    // Validate portfolio title
    if (!portfolioTitle.trim()) {
      setError(t('portfolios.edit.enterTitle'));
      setIsSaving(false);
      return;
    }

    try {
      // Extract all symbols
      const symbols = localAllocations.map((allocation) => allocation.symbol.trim()).filter((sym) => sym !== '');
      const uniqueSymbols = [...new Set(symbols)];

      // Allow empty portfolios to be saved (as a watchlist with no symbols)
      // User can add symbols later

      // Check for invalid allocations (but allow empty/undefined allocations)
      // This allows watchlists with undefined allocations to be saved
      const invalidIndices = Object.entries(allocationStatus)
        .filter(([, status]) => !status.isEmpty && !status.isValid)
        .map(([index]) => parseInt(index));

      if (invalidIndices.length > 0) {
        setError(t('portfolios.edit.invalidAllocations'));
        setIsSaving(false);
        return;
      }

      // Check for duplicate symbols
      if (uniqueSymbols.length !== symbols.length) {
        setError(t('portfolios.edit.duplicateSymbols'));
        setIsSaving(false);
        return;
      }

      // Validate all symbols with Algolia before saving
      const validationResults = await Promise.all(
        uniqueSymbols.map(async (symbol) => ({
          symbol,
          isValid: await validateTicker(symbol),
        })),
      );

      const invalidSymbols = validationResults.filter((result) => !result.isValid).map((result) => result.symbol);

      if (invalidSymbols.length > 0) {
        setError(t('portfolios.edit.invalidSymbols', { symbols: invalidSymbols.join(', ') }));
        setIsSaving(false);
        return;
      }

      // Validate all symbols with the API (this will fetch additional data)
      const investments = await getBasicInvestments(uniqueSymbols);

      // Update symbol names from API response
      const updatedAllocations = localAllocations
        .filter((allocation) => allocation.symbol.trim() !== '')
        .map((allocation) => {
          const investment = investments.find((inv) => inv.symbol.toUpperCase() === allocation.symbol.toUpperCase());
          return {
            ...allocation,
            symbol: allocation.symbol.toUpperCase(),
            name: investment?.name || allocation.name || '',
          };
        });

      // Update portfolio with new title, allocations and symbols
      const updatedPortfolio: storage.Portfolio = {
        ...portfolio,
        title: portfolioTitle.trim(), // Use the updated title
        strategy: '', // Strategy field kept for compatibility but empty
        allocations: updatedAllocations,
        symbols: updatedAllocations.map((allocation) => allocation.symbol),
      };

      // For a new portfolio, use savePortfolio instead of updatePortfolio
      let success = false;
      let newId = id;

      if (id === 'new') {
        // This is a new portfolio, save it with a new ID
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _id, ...restPortfolio } = updatedPortfolio;
        newId = await storage.savePortfolio({
          ...restPortfolio,
          symbols: updatedAllocations.map((allocation) => allocation.symbol),
          allocations: updatedAllocations,
        });
        success = !!newId;
      } else {
        // Update existing portfolio
        success = await storage.updatePortfolio(updatedPortfolio);
      }

      if (success) {
        // Select this portfolio and redirect to main portfolios page
        localStorage.setItem(PORTFOLIO_STORAGE_KEY, newId);
        history.push('/portfolios');
      } else {
        setError(t('portfolios.edit.failedSave'));
      }
    } catch (err) {
      console.error('Error saving portfolio:', err);
      setError(t('portfolios.edit.errorSaving'));
    } finally {
      setIsSaving(false);
    }
  };

  const pageStyles = css`
    padding-bottom: calc(${spacing.regular} + ${spacing.giga} + 300px);
    background-color: ${colors.sugar};
    html[data-theme='dark'] & {
      background-color: ${colors.liquorice};
    }
  `;

  // Removed unused headingStyles

  const errorStyles = css`
    ${fontStyles.dog};
    color: ${colors.berry};
    margin: ${spacing.deci} 0;
    padding: ${spacing.centi};
    background-color: ${colors.berryAir};
    border-radius: 4px;
  `;

  const loadingStyles = css`
    ${fontStyles.dog};
    text-align: center;
    margin: ${spacing.deka} 0;
    color: var(--secondary-text);
  `;

  const allocationItemStyles = css`
    padding: ${spacing.milli} 0;
    margin-bottom: ${spacing.deci};
    width: 100%; /* Take full width of container */

    html[data-theme='dark'] & {
      border-bottom: 1px solid ${colors.tungsten}40;
    }
  `;

  const inputGroupStyles = css`
    margin-bottom: ${spacing.centi};
  `;

  const rowStyles = css`
    display: grid;
    grid-template-columns: 3fr 2fr auto;
    column-gap: ${spacing.centi};
    align-items: center;
    width: 100%;
    padding: ${spacing.micro} 0;
  `;

  const tickerColumnStyles = css`
    display: flex;
    flex-direction: column;
  `;

  const allocationColumnStyles = css`
    display: flex;
    flex-direction: column;
  `;

  const cellInputStyles = css`
    border-radius: 0;
    padding: ${spacing.milli};
  `;

  const srOnlyStyles = css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    border: 0;
  `;

  const labelStyles = css`
    ${fontStyles.cat};
    color: var(--secondary-text);
    display: block;
    margin-bottom: ${spacing.micro};
    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `;

  const inputStyles = css`
    width: 100%;
    padding: ${spacing.centi};
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background-color: ${colors.snow} !important;
    color: var(--text-color);
    ${fontStyles.dog};
    font-size: 16px; /* Prevent iOS zoom on focus */

    @media (min-width: 480px) {
      font-size: 0.95rem; /* Revert to design size on larger screens */
    }

    &:focus {
      outline: none;
      border-color: ${colors.petrolBlue};
    }

    html[data-theme='dark'] & {
      background-color: ${colors.tungsten}30 !important;
      border-color: ${colors.tungsten}90;
      color: ${colors.sugar};

      &::placeholder {
        color: ${colors.silver}90;
      }
    }
  `;

  const validInputStyles = css`
    border-color: ${colors.leaf};

    &:focus {
      border-color: ${colors.leaf};
    }

    html[data-theme='dark'] & {
      border-color: ${colors.leaf}CC;
    }
  `;

  const invalidInputStyles = css`
    border-color: ${colors.berry};

    &:focus {
      border-color: ${colors.berry};
    }

    html[data-theme='dark'] & {
      border-color: ${colors.berry}CC;
    }
  `;

  const emptyInputStyles = css`
    border-color: ${colors.orange}90;

    &:focus {
      border-color: ${colors.orange};
    }

    html[data-theme='dark'] & {
      border-color: ${colors.orange}CC;
    }
  `;

  const validationIconStyles = css`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  `;

  const removeIconButtonStyles = css`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: ${spacing.micro};
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 20px;
      height: 20px;
      filter: var(--icon-filter);
    }
  `;

  const actionButtonsStyles = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: ${spacing.deka};
    position: sticky;
    bottom: 0;
    background-color: ${colors.sugar};
    padding: ${spacing.deci} 0;
    border-top: 1px solid ${colors.cloud};
    z-index: 10;

    html[data-theme='dark'] & {
      background-color: ${colors.liquorice};
      border-top: 1px solid ${colors.tungsten}40;
    }
  `;

  const addTickerButtonStyles = css`
    width: 100%;
    background-color: ${colors.snow};
    color: ${colors.petrolBlue};
    border: 1px dashed ${colors.petrolBlue};
    border-radius: 8px;
    padding: ${spacing.centi} ${spacing.deci};
    margin-bottom: ${spacing.deci};
    cursor: pointer;
    ${fontStyles.dog};
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;

    &:hover {
      background-color: ${colors.petrolBlue}06;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }

    html[data-theme='dark'] & {
      background-color: ${colors.tungsten}30;
      color: ${colors.skyBlue};
      border-color: ${colors.skyBlue}80;

      &:hover {
        background-color: ${colors.skyBlue}22;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
      }
    }
  `;

  if (isLoading) {
    return (
      <Page
        css={css`
          background-color: ${colors.sugar};

          html[data-theme='dark'] & {
            background-color: ${colors.liquorice};
          }
        `}
      >
        <Header
          headingText={id === 'new' ? t('portfolios.edit.createTitle') : t('portfolios.edit.editTitle')}
          onBack={handleBack}
          css={css`
            background-color: ${colors.sugar} !important;

            html[data-theme='dark'] & {
              background-color: ${colors.liquorice} !important;
            }
          `}
        />
        <div css={pageStyles}>
          <div css={loadingStyles}>{t('portfolios.edit.loading')}</div>
        </div>
        <NavigationBar />
      </Page>
    );
  }

  if (!portfolio) {
    return (
      <Page
        css={css`
          background-color: ${colors.sugar};

          html[data-theme='dark'] & {
            background-color: ${colors.liquorice};
          }
        `}
      >
        <Header
          headingText={t('portfolios.edit.notFound')}
          onBack={handleBack}
          css={css`
            background-color: ${colors.sugar} !important;

            html[data-theme='dark'] & {
              background-color: ${colors.liquorice} !important;
            }
          `}
        />
        <div css={pageStyles}>
          <div css={errorStyles}>{t('portfolios.edit.notFoundMessage')}</div>
        </div>
        <NavigationBar />
      </Page>
    );
  }

  return (
    <Page
      css={css`
        background-color: ${colors.sugar};

        html[data-theme='dark'] & {
          background-color: ${colors.liquorice};
        }
      `}
    >
      <Header
        headingText={id === 'new' ? t('portfolios.edit.createTitle') : t('portfolios.edit.editTitle')}
        onBack={handleBack}
        css={css`
          background-color: ${colors.sugar} !important;

          html[data-theme='dark'] & {
            background-color: ${colors.liquorice} !important;
          }
        `}
      />
      <div css={pageStyles}>
        <div css={inputGroupStyles}>
          <label css={labelStyles} htmlFor="portfolio-title">
            {t('portfolios.edit.portfolioTitle')}
          </label>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              margin-bottom: ${spacing.deci};
            `}
          >
            <input
              id="portfolio-title"
              css={[
                inputStyles,
                css`
                  width: 100%; /* Full width on small screens */
                `,
              ]}
              type="text"
              value={portfolioTitle}
              onChange={(e) => setPortfolioTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder={t('portfolios.edit.portfolioNamePlaceholder')}
              required
            />
          </div>
        </div>

        <div>
          {localAllocations.map((allocation, index) => (
            <div key={index} css={allocationItemStyles}>
              {/* Row with ticker and allocation */}
              <div
                css={css`
                  ${rowStyles};
                  position: relative;
                `}
              >
                <div css={tickerColumnStyles}>
                  <label css={[labelStyles, index > 0 && srOnlyStyles]} htmlFor={`symbol-${index}`}>
                    {t('portfolios.edit.symbolLabel')}
                  </label>
                  <div
                    css={css`
                      position: relative;
                    `}
                  >
                    <input
                      id={`symbol-${index}`}
                      css={[
                        inputStyles,
                        cellInputStyles,
                        validationStatus[index]?.isValid && validInputStyles,
                        validationStatus[index]?.isValid === false &&
                          !validationStatus[index]?.isValidating &&
                          invalidInputStyles,
                        css`
                          text-transform: uppercase;
                        `,
                      ]}
                      type="text"
                      value={allocation.symbol}
                      onChange={(e) => handleSymbolChange(index, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="AAPL"
                      maxLength={6}
                    />
                    {validationStatus[index]?.isValidating && (
                      <span css={validationIconStyles}>
                        <span
                          css={css`
                            display: inline-block;
                            width: 16px;
                            height: 16px;
                            border: 2px solid ${colors.petrolBlue};
                            border-top-color: transparent;
                            border-radius: 50%;
                            animation: spin 0.8s linear infinite;
                            @keyframes spin {
                              0% {
                                transform: rotate(0deg);
                              }
                              100% {
                                transform: rotate(360deg);
                              }
                            }
                          `}
                        ></span>
                      </span>
                    )}
                    {validationStatus[index]?.isValid && !validationStatus[index]?.isValidating && (
                      <span css={validationIconStyles}>
                        <span
                          css={css`
                            color: ${colors.leaf};
                            font-size: 18px;
                          `}
                        >
                          ✓
                        </span>
                      </span>
                    )}
                    {validationStatus[index]?.isValid === false &&
                      !validationStatus[index]?.isValidating &&
                      allocation.symbol.trim() !== '' && (
                        <span css={validationIconStyles} title={t('portfolios.edit.invalidTicker')}>
                          <span
                            css={css`
                              color: ${colors.berry};
                              font-size: 18px;
                            `}
                          >
                            ✗
                          </span>
                        </span>
                      )}
                  </div>
                </div>

                <div css={allocationColumnStyles}>
                  <label css={[labelStyles, index > 0 && srOnlyStyles]} htmlFor={`allocation-${index}`}>
                    {t('portfolios.edit.allocationLabel')}
                  </label>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      width: 100%;
                    `}
                  >
                    <div
                      css={css`
                        position: relative;
                        width: 100%;
                      `}
                    >
                      <input
                        id={`allocation-${index}`}
                        css={[
                          inputStyles,
                          cellInputStyles,
                          allocationStatus[index]?.isEmpty && emptyInputStyles,
                          allocationStatus[index]?.isValid && validInputStyles,
                          allocationStatus[index]?.isValid === false &&
                            !allocationStatus[index]?.isEmpty &&
                            invalidInputStyles,
                        ]}
                        type="text" /* Changed from number to allow empty values */
                        value={
                          allocationStatus[index]?.isEmpty || allocation.allocation_percentage === undefined
                            ? ''
                            : allocation.allocation_percentage.toString()
                        }
                        onChange={(e) => handleAllocationChange(index, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        placeholder={t('portfolios.edit.allocationPlaceholder')}
                      />
                      {allocationStatus[index]?.isEmpty && (
                        <span css={validationIconStyles} title={t('portfolios.edit.enterPercentage')}>
                          <span
                            css={css`
                              color: ${colors.orange};
                              font-size: 18px;
                            `}
                          >
                            !
                          </span>
                        </span>
                      )}
                      {allocationStatus[index]?.isValid === false && !allocationStatus[index]?.isEmpty && (
                        <span css={validationIconStyles} title={t('portfolios.edit.allocationRange')}>
                          <span
                            css={css`
                              color: ${colors.berry};
                              font-size: 18px;
                            `}
                          >
                            !
                          </span>
                        </span>
                      )}
                    </div>
                    <button
                      css={removeIconButtonStyles}
                      onClick={() => handleRemove(index)}
                      type="button"
                      aria-label={t('portfolios.edit.removeAriaLabel')}
                    >
                      <img src={deleteIcon} alt={t('portfolios.edit.deleteAlt')} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Always show the Add Ticker button */}
          <button css={addTickerButtonStyles} onClick={handleAddTicker} type="button">
            <span
              css={css`
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 22px;
                height: 22px;
                margin-right: ${spacing.centi};
                border-radius: 50%;
                background-color: ${colors.petrolBlue}22;
                color: ${colors.petrolBlue};
                font-weight: bold;
                line-height: 1;

                html[data-theme='dark'] & {
                  background-color: ${colors.skyBlue}22;
                  color: ${colors.skyBlue};
                }
              `}
            >
              +
            </span>
            {t('portfolios.edit.addTicker')}
          </button>
        </div>

        <div css={actionButtonsStyles}>
          {error && <div css={errorStyles}>{error}</div>}
          <Button
            onClick={handleSave}
            disabled={isSaving}
            css={css`
              width: 200px;
              background-color: ${colors.petrolBlue};
              color: ${colors.snow};
              font-weight: 500;
              padding: ${spacing.centi} ${spacing.deci};
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              transition: all 0.2s ease;

              &:hover:not(:disabled) {
                background-color: ${colors.petrolBlue}ee;
                box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
                transform: translateY(-1px);
              }

              &:active:not(:disabled) {
                transform: translateY(0);
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              }

              &:disabled {
                opacity: 0.7;
              }

              html[data-theme='dark'] & {
                background-color: ${colors.skyBlue};
                color: ${colors.sugar};
                box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);

                &:hover:not(:disabled) {
                  background-color: ${colors.skyBlue}ee;
                  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.2);
                }
              }
            `}
          >
            {isSaving ? t('portfolios.edit.saving') : t('portfolios.edit.saveChanges')}
          </Button>
        </div>
      </div>
      <NavigationBar />
    </Page>
  );
};

export default EditPortfolio;
